import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Sparkles, Lock } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';

// Premium Gate Component - Blocks access to premium features for free users
export const PremiumGate = ({ 
  featureKey, 
  children, 
  fallback = null,
  showUpgradePrompt = true 
}) => {
  const navigate = useNavigate();
  const { hasAccess, getFeatureInfo, loading } = useSubscription();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (hasAccess(featureKey)) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  const featureInfo = getFeatureInfo(featureKey);
  const featureName = featureInfo?.name || 'This feature';
  const requiredPlan = featureInfo?.minPlan || 'Pro';

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
        <div className="text-center p-8 max-w-md">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
            <Crown className="text-white" size={32} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Premium Feature</h3>
          <p className="text-slate-600 mb-4">
            {featureName} requires a <span className="font-semibold text-amber-600 capitalize">{requiredPlan}</span> subscription to access.
          </p>
          <button 
            onClick={() => navigate('/subscription')}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl"
          >
            <Sparkles size={18} className="inline mr-2" />
            Upgrade to {requiredPlan}
          </button>
          <p className="text-xs text-slate-500 mt-3">
            Unlock all premium features and accelerate your ESG reporting
          </p>
        </div>
      </div>
      <div className="opacity-20 pointer-events-none blur-sm">
        {children}
      </div>
    </div>
  );
};

// Premium Badge Component - Shows PRO/ENTERPRISE badge on navigation items
export const PremiumBadge = ({ plan = 'pro', size = 'sm' }) => {
  const sizeClasses = {
    xs: 'text-[9px] px-1 py-0.5',
    sm: 'text-[10px] px-1.5 py-0.5',
    md: 'text-xs px-2 py-1'
  };

  const planColors = {
    pro: 'bg-gradient-to-r from-amber-500 to-orange-500',
    enterprise: 'bg-gradient-to-r from-purple-500 to-indigo-500'
  };

  return (
    <span className={`${sizeClasses[size]} ${planColors[plan]} text-white font-bold rounded uppercase`}>
      {plan === 'enterprise' ? 'ENT' : 'PRO'}
    </span>
  );
};

// Locked Feature Overlay - Shows lock icon on disabled features
export const LockedOverlay = ({ featureKey, children }) => {
  const { hasAccess } = useSubscription();

  if (hasAccess(featureKey)) {
    return children;
  }

  return (
    <div className="relative group cursor-not-allowed">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="bg-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <Lock size={14} className="text-amber-500" />
          <span className="text-xs font-medium text-slate-700">Premium Feature</span>
        </div>
      </div>
    </div>
  );
};

// Hook for checking premium access in components
export const usePremiumAccess = (featureKey) => {
  const { hasAccess, getFeatureInfo, isPremium, isEnterprise } = useSubscription();
  
  return {
    hasAccess: hasAccess(featureKey),
    featureInfo: getFeatureInfo(featureKey),
    isPremium: isPremium(),
    isEnterprise: isEnterprise()
  };
};

export default PremiumGate;
